import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  last,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  takeWhile,
  throwError,
  timer,
} from 'rxjs';
import { DataTableModuleConfiguration } from '@nimmsta/ng-commons-lib/data-access';
import {
  WorkflowAdminJobExportRequest,
  WorkflowJobExportRequest,
  WorkflowJobExportStatus,
  WorkflowJobExportStatusResponse,
} from '../models/jobExport';

@Injectable({
  providedIn: 'root',
})
export class JobExportService {
  private static readonly POLL_INTERVAL_MS = 2000;

  private static readonly POLL_TIMEOUT_MS = 10 * 60 * 1000;

  private http = inject(HttpClient);

  private configuration = inject(DataTableModuleConfiguration);

  private readonly baseUrl: string;

  constructor() {
    if (!this.configuration.apiUrls.workflow) {
      throw new Error('Workflow API URL is not configured');
    }
    this.baseUrl = `${this.configuration.apiUrls.workflow}/api/v1/jobs`;
  }

  startRegularExport(
    request: WorkflowJobExportRequest,
  ): Observable<WorkflowJobExportStatusResponse> {
    return this.http
      .post<WorkflowJobExportStatusResponse>(`${this.baseUrl}/export/start`, {
        ...request,
        mode: request.mode ?? 'AUTO',
      })
      .pipe(
        switchMap((initialStatus) =>
          this.pollUntilFinalStatus(initialStatus, (exportId) =>
            this.getRegularExportStatus(exportId),
          ),
        ),
      );
  }

  startAdminExport(
    request: WorkflowAdminJobExportRequest,
  ): Observable<WorkflowJobExportStatusResponse> {
    return this.http
      .post<WorkflowJobExportStatusResponse>(
        `${this.baseUrl}/admin/csv/start`,
        {
          ...request,
          mode: request.mode ?? 'AUTO',
        },
      )
      .pipe(
        switchMap((initialStatus) =>
          this.pollUntilFinalStatus(initialStatus, (exportId) =>
            this.getAdminExportStatus(exportId),
          ),
        ),
      );
  }

  downloadExportFile(downloadUrl: string): void {
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private getRegularExportStatus(
    exportId: string,
  ): Observable<WorkflowJobExportStatusResponse> {
    return this.http.get<WorkflowJobExportStatusResponse>(
      `${this.baseUrl}/export/async/${exportId}`,
    );
  }

  private getAdminExportStatus(
    exportId: string,
  ): Observable<WorkflowJobExportStatusResponse> {
    return this.http.get<WorkflowJobExportStatusResponse>(
      `${this.baseUrl}/admin/csv/async/${exportId}`,
    );
  }

  private pollUntilFinalStatus(
    initialStatus: WorkflowJobExportStatusResponse,
    statusProvider: (
      exportId: string,
    ) => Observable<WorkflowJobExportStatusResponse>,
  ): Observable<WorkflowJobExportStatusResponse> {
    if (!this.isPendingOrRunning(initialStatus.status)) {
      return of(initialStatus);
    }

    const maxPolls =
      Math.ceil(
        JobExportService.POLL_TIMEOUT_MS / JobExportService.POLL_INTERVAL_MS,
      ) + 1;

    return timer(0, JobExportService.POLL_INTERVAL_MS).pipe(
      take(maxPolls),
      switchMap((pollIteration) => {
        if (pollIteration === 0) {
          return of(initialStatus);
        }
        return statusProvider(initialStatus.id);
      }),
      takeWhile(
        (currentStatus) => this.isPendingOrRunning(currentStatus.status),
        true,
      ),
      last(),
      mergeMap((lastStatus) => {
        if (this.isPendingOrRunning(lastStatus.status)) {
          return throwError(() => this.createPollingTimeoutError());
        }
        return of(lastStatus);
      }),
    );
  }

  private isPendingOrRunning(status: WorkflowJobExportStatus): boolean {
    return status === 'PENDING' || status === 'RUNNING';
  }

  private createPollingTimeoutError(): HttpErrorResponse {
    return new HttpErrorResponse({
      status: 408,
      statusText: 'Request Timeout',
      error: {
        error: 'httperror.408',
      },
    });
  }
}
